export default class Countdown {
  constructor(container) {
    // DOM
    this.DOM = {
      container,
      time: container.querySelector('.Countdown__target'),
      timer: container.querySelector('.Countdown__timer'),
      units: container.querySelectorAll('[data-countdown-unit]'),
      content: container.querySelector('.Countdown__success'),
    }

    if (!this.DOM.container) return

    this.interval = null
    this.targetDate = null
    this.unitElements = {}

    this.init()
  }

  init() {
    if (!this.DOM.time || !this.DOM.timer || !this.DOM.units.length) return

    const datetime = this.DOM.time.getAttribute('datetime') || this.DOM.time.textContent

    if (!datetime) return

    this.targetDate = this.parseDate(datetime)

    if (!this.targetDate || Number.isNaN(this.targetDate.getTime())) return

    this.DOM.units.forEach(unit => {
      const key = unit.getAttribute('data-countdown-unit')

      if (!key) return

      this.unitElements[key] = {
        container: unit,
        value: unit.querySelector('.Countdown__value'),
        label: unit.querySelector('.Countdown__label'),
        singular: unit.dataset.labelSingular,
        plural: unit.dataset.labelPlural,
      }
    })

    this.toggleContent(false)
    this.DOM.container.classList.add('is-ready')
    this.DOM.time.setAttribute('aria-hidden', 'true')
    this.updateTimerVisibility(true)

    this.update()

    if (this.isExpired()) {
      this.complete()
      return
    }

    this.interval = window.setInterval(() => {
      this.update()
    }, 1000)
  }

  parseDate(value) {
    const formattedValue = value.trim().replace(' ', 'T')

    return new Date(formattedValue)
  }

  isExpired() {
    return !this.targetDate || this.targetDate.getTime() <= Date.now()
  }

  getTimeParts() {
    const now = new Date()

    if (this.isExpired()) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      }
    }

    const diffMs = Math.max(this.targetDate.getTime() - now.getTime(), 0)
    const msInSecond = 1000
    const msInMinute = msInSecond * 60
    const msInHour = msInMinute * 60
    const msInDay = msInHour * 24

    const days = Math.floor(diffMs / msInDay)
    const hours = Math.floor((diffMs % msInDay) / msInHour)
    const minutes = Math.floor((diffMs % msInHour) / msInMinute)
    const seconds = Math.floor((diffMs % msInMinute) / msInSecond)

    return {
      days,
      hours,
      minutes,
      seconds,
    }
  }

  update() {
    if (!this.targetDate) return

    if (this.isExpired()) {
      this.complete()
      return
    }

    const parts = this.getTimeParts()

    Object.entries(parts).forEach(([key, value]) => {
      const element = this.unitElements[key]
      if (!element || !element.value) return

      const shouldPad = ['days', 'hours', 'minutes', 'seconds'].includes(key)
      const formattedValue = shouldPad ? String(value).padStart(2, '0') : String(value)

      element.value.textContent = formattedValue

      if (element.label) {
        const { singular, plural } = element
        const multi = value > 1
        const labelText = multi ? (plural || `${singular || ''}s`) : (singular || plural || '')

        element.label.textContent = labelText
      }
    })
  }

  complete() {
    this.destroy({ reset: false })
    this.updateTimerVisibility(false)
    this.toggleContent(true)
    this.DOM.container.classList.add('is-complete')
    this.DOM.time && this.DOM.time.setAttribute('aria-hidden', 'true')
  }

  toggleContent(isVisible) {
    if (!this.DOM.content) return

    if (isVisible) {
      this.DOM.content.classList.add('is-visible')
      this.DOM.content.removeAttribute('hidden')
    } else {
      this.DOM.content.classList.remove('is-visible')
      this.DOM.content.setAttribute('hidden', 'true')
    }
  }

  updateTimerVisibility(isVisible) {
    if (!this.DOM.timer) return

    if (isVisible) {
      this.DOM.timer.classList.remove('is-hidden')
      this.DOM.timer.setAttribute('aria-hidden', 'false')
    } else {
      this.DOM.timer.classList.add('is-hidden')
      this.DOM.timer.setAttribute('aria-hidden', 'true')
    }
  }

  reset() {
    const { container, time, timer, content } = this.DOM

    container && container.classList.remove('is-ready', 'is-complete')

    if (time) {
      time.removeAttribute('aria-hidden')
      time.style.removeProperty('display')
    }

    if (timer) {
      timer.classList.remove('is-hidden')
      timer.setAttribute('aria-hidden', 'false')
    }

    if (content) {
      content.classList.remove('is-visible')
      content.setAttribute('hidden', 'true')
    }

    Object.values(this.unitElements).forEach(unit => {
      if (!unit) return

      if (unit.value) unit.value.textContent = '00'

      if (unit.label) {
        const { singular, plural } = unit
        unit.label.textContent = singular || plural || ''
      }
    })
  }

  /**
   * Destroy
   */
  destroy({ reset = true } = {}) {
    if (this.interval) {
      window.clearInterval(this.interval)
      this.interval = null
    }

    if (reset) {
      this.reset()
    }
  }
}
